
import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import { hasEnterpriseSubscription, Organization } from "@gsx/common";
import LoadingOverlay from "../../../../common/src/components/LoadingOverlay.vue";
import { OrganizationListItem } from "@gsx/common/src/types/http/dashboard/organization";
import { Container } from "inversify";
import { OrganizationListStore } from "../../stores/organization/OrganizationListStore";
import NoRecords from "../../../../common/src/components/NoRecords.vue";
import { CollectionStore } from "../../../../common/src/stores/CollectionStore";
import { enrichedColumns } from "../../utils/tableUtils";
import { ProductTypeStore } from "../../../../common/src/stores/ProductTypeStore";
import { handleError } from "../../../../common/src/utility/errorUtils";
import { ServiceTypeStore } from "../../../../common/src/stores/ServiceTypeStore";
import { isNthLevel } from "../../../../common/src/utility/treeUtils";
import { AuthStore } from "../../../../common/src/stores/AuthStore";
import { OrganizationFilterStore } from "../../stores/organization/OrganizationFilterStore";
import { debounce } from "debounce";

interface Column {
    id?: keyof Organization;
    name: string;
}

@Component({
    components: { NoRecords, LoadingOverlay },
})
export default class OrganizationTable extends Vue {
    @Prop() collectionStore!: CollectionStore<OrganizationListItem>;
    @Inject("container") readonly container!: Container;
    organizationListStore: OrganizationListStore = this.container.get(
        "OrganizationExplorerListStore",
    );
    organizationFilterStore: OrganizationFilterStore = this.container.get(OrganizationFilterStore);
    productTypeStore: ProductTypeStore = this.container.get(ProductTypeStore);
    serviceTypeStore: ServiceTypeStore = this.container.get(ServiceTypeStore);
    authStore: AuthStore = this.container.get(AuthStore);

    readonly columns: ReadonlyArray<Column> = [
        {
            id: "organizationName",
            name: "Name",
        },
        {
            id: "country",
            name: "Country",
        },
        {
            name: "Product Tiers",
        },
        {
            name: "Services",
        },
    ];

    created() {
        this.productTypeStore.load().catch(handleError);
        this.serviceTypeStore.load().catch(handleError);
        this.organizationListStore.pageSize = 10;
        this.organizationListStore.load().catch(handleError);
    }

    get enrichedColumns() {
        return enrichedColumns(this.collectionStore, this.columns);
    }

    get organizations() {
        if (hasEnterpriseSubscription(this.authStore.user)) {
            return this.organizationListStore.items || [];
        }

        return [];
    }

    reload() {
        this.organizationListStore.reload().catch(handleError);
    }

    @Watch("organizationListStore.query")
    @Watch("organizationFilterStore.serviceTags")
    @Watch("organizationFilterStore.productTags")
    @Watch("organizationFilterStore.operatingRegimeTags")
    @Watch("organizationFilterStore.applicationTags")
    @Watch("organizationFilterStore.countries")
    @Watch("organizationFilterStore.states")
    @Watch("organizationFilterStore.logicalOperator")
    @Watch("organizationFilterStore.afterYear")
    @Watch("organizationFilterStore.beforeYear")
    reloadDebounce = debounce(this.reload, 300);

    getProductTags(organization: OrganizationListItem): string[] {
        return (organization.productTags || [])
            .filter((tag) => isNthLevel(tag, 1))
            .map((tag) => this.productTypeStore.idLabelMapping.get(tag) || tag);
    }

    getServiceTags(organization: OrganizationListItem): string[] {
        return (organization.serviceTags || [])
            .filter((tag) => isNthLevel(tag, 1))
            .map((tag) => this.serviceTypeStore.idLabelMapping.get(tag) || tag);
    }

    getOrganizationUrl(organization: OrganizationListItem): string {
        return `/organizations/${organization.organizationid}`;
    }
}
